import { useState, useMemo, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  CircularProgress,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Menu,
  TextField,
  InputAdornment,
} from "@mui/material";
import { format } from "date-fns";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import toast from "react-hot-toast";
import StatusBadge from "../../components/StatusBadge/StatusBadge";
import ZohoSyncStatus from "../../components/ZohoSyncStatus/ZohoSyncStatus";
import { useNavigate } from "react-router-dom";
import { getStatusDisplayLabel } from "../../constant";
import CommonTablePagination from "../../components/CommonTablePagination/CommonTablePagination";
import { loadDashboardTableState, saveDashboardTableState } from "../../utils/dashboardTableState";
import { exportRowsToExcel } from "../../utils/exportToExcel";
import {
  getDashboardExportFileName,
  mapDashboardRowForExport,
} from "../../utils/dashboardExport";
import { parseApiDate } from "../../utils/dateFormatUtils";

/* ============================
   HELPERS
============================ */

const DATE_FIELD_KEYS = {
  scheduledDate: ["scheduledDate", "ScheduledDate"],
  completedAt: ["completedAt", "CompletedAt"],
};

const getRowDateRaw = (row, field) => {
  const keys = DATE_FIELD_KEYS[field] ?? [field];
  for (const key of keys) {
    const value = row?.[key];
    if (value != null && value !== "") return value;
  }
  return null;
};

const getRowSortDate = (row, field) => {
  const parsed = parseApiDate(getRowDateRaw(row, field));
  return parsed ? parsed.getTime() : null;
};

const formatDate = (date) =>
  date ? format(new Date(date), "MM/dd/yyyy") : null;

const formatScore = (value) =>
 Math.floor(value * 10) / 10;

/* ============================
   COMPONENT
============================ */

const CandidateInterviewsTable = ({ interviews, loading, onReschedule }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [page, setPage] = useState(() => loadDashboardTableState().page);
  const [rowsPerPage, setRowsPerPage] = useState(() => loadDashboardTableState().rowsPerPage);
  const [statusFilter, setStatusFilter] = useState(() => loadDashboardTableState().statusFilter);
  const [jobTitleFilter, setJobTitleFilter] = useState(
    () => loadDashboardTableState().jobTitleFilter,
  );
  const [nameQuery, setNameQuery] = useState(() => loadDashboardTableState().nameQuery);

  useEffect(() => {
    saveDashboardTableState({
      page,
      rowsPerPage,
      statusFilter,
      jobTitleFilter,
      nameQuery,
      fromDashboard: false,
    });
  }, [page, rowsPerPage, statusFilter, jobTitleFilter, nameQuery]);

  const [sortConfig, setSortConfig] = useState({
    field: "scheduledDate",
    direction: "asc",
  });
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
  const [selectedRowForActions, setSelectedRowForActions] = useState(null);

  const allRows = interviews?.interviews || [];

  const jobTitleFilterOptions = useMemo(() => {
    const titles = new Set();
    allRows.forEach((row) => {
      const title = (row.jobTitle ?? "").trim();
      if (title) titles.add(title);
    });

    return [
      { value: "", label: "All" },
      ...Array.from(titles)
        .sort((a, b) => a.localeCompare(b))
        .map((title) => ({ value: title, label: title })),
    ];
  }, [allRows]);

  const statusFilterOptions = useMemo(() => {
    const statuses = new Set();
    allRows.forEach((row) => {
      const status = (row.status ?? "").trim();
      if (status) statuses.add(status);
    });

    return [
      { value: "", label: "All" },
      ...Array.from(statuses)
        .sort((a, b) =>
          getStatusDisplayLabel(a).localeCompare(getStatusDisplayLabel(b)),
        )
        .map((status) => ({
          value: status,
          label: getStatusDisplayLabel(status),
        })),
    ];
  }, [allRows]);

  useEffect(() => {
    if (!jobTitleFilter) return;
    if (!jobTitleFilterOptions.some((opt) => opt.value === jobTitleFilter)) {
      setJobTitleFilter("");
    }
  }, [jobTitleFilter, jobTitleFilterOptions]);

  useEffect(() => {
    if (!statusFilter) return;
    if (!statusFilterOptions.some((opt) => opt.value === statusFilter)) {
      setStatusFilter("");
    }
  }, [statusFilter, statusFilterOptions]);

  /* ============================
     FILTER + SORT + PAGINATION
  ============================ */

  const filteredRows = useMemo(() => {
    const q = nameQuery.trim().toLowerCase();

    return allRows.filter((row) => {
      const matchesStatus = statusFilter ? row.status === statusFilter : true;
      const matchesJobTitle = jobTitleFilter
        ? (row.jobTitle ?? "").trim() === jobTitleFilter
        : true;
      const matchesName = q
        ? (row.candidateName ?? "").toLowerCase().includes(q)
        : true;
      return matchesStatus && matchesJobTitle && matchesName;
    });
  }, [allRows, statusFilter, jobTitleFilter, nameQuery]);

  const sortedRows = useMemo(() => {
    if (!sortConfig.field) return filteredRows;

    const { field, direction } = sortConfig;
    // asc (↑) = newest first, desc (↓) = oldest first
    const factor = direction === "asc" ? -1 : 1;

    return [...filteredRows].sort((a, b) => {
      const aTime = getRowSortDate(a, field);
      const bTime = getRowSortDate(b, field);

      // Rows without a date (e.g. not completed) always sort to the bottom.
      if (aTime == null && bTime == null) return 0;
      if (aTime == null) return 1;
      if (bTime == null) return -1;

      return (aTime - bTime) * factor;
    });
  }, [filteredRows, sortConfig]);

  const maxPage = useMemo(() => {
    if (sortedRows.length === 0) return 0;
    return Math.max(0, Math.ceil(sortedRows.length / rowsPerPage) - 1);
  }, [sortedRows.length, rowsPerPage]);

  const safePage = Math.min(page, maxPage);

  // Wait until interviews load — otherwise maxPage is 0 and page gets reset to 1.
  useEffect(() => {
    if (loading) return;
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [page, maxPage, loading]);

  const paginatedRows = useMemo(() => {
    return sortedRows.slice(
      safePage * rowsPerPage,
      safePage * rowsPerPage + rowsPerPage,
    );
  }, [sortedRows, safePage, rowsPerPage]);

  /* ============================
     HANDLERS
  ============================ */

  const handleSortByDate = (field) => {
    setSortConfig((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
    setPage(0);
  };

  const handleOpenActionsMenu = (event, row) => {
    setActionMenuAnchorEl(event.currentTarget);
    setSelectedRowForActions(row);
  };

  const handleCloseActionsMenu = () => {
    setActionMenuAnchorEl(null);
    setSelectedRowForActions(null);
  };

  const handleRescheduleClick = () => {
    if (selectedRowForActions) onReschedule(selectedRowForActions);
    handleCloseActionsMenu();
  };

  const handleExportToExcel = () => {
    if (loading) return;

    if (sortedRows.length === 0) {
      toast.error("No data to export. Adjust your filters or try again later.");
      return;
    }

    const exportRows = sortedRows.map(mapDashboardRowForExport);
    const exported = exportRowsToExcel(exportRows, {
      sheetName: "Candidates",
      fileName: getDashboardExportFileName(),
    });

    if (exported) {
      toast.success(`Exported ${exportRows.length} row(s) to Excel.`);
    }
  };

  /* ============================
     TABLE BODY STATES
  ============================ */

  const renderLoading = () => (
    <TableRow>
      <TableCell colSpan={10}>
        <Box
          sx={{
            py: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <CircularProgress size={40} thickness={4} sx={{ color: "primary.main" }} />
          <Typography sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
            Loading candidate interviews…
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );

  const renderEmpty = (text) => (
    <TableRow>
      <TableCell colSpan={10}>
        <Typography
          sx={{
            fontWeight: 500,
            color: theme.palette.text.secondary,
            fontSize: "0.9375rem",
          }}
        >
          {text}
        </Typography>
      </TableCell>
    </TableRow>
  );

  /* ============================
     RENDER
  ============================ */

  return (
    <Box sx={{ mt: 3 }}>
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 3,
          overflow: "hidden",
          backgroundColor: theme.palette.background.paper,
        }}
      >
        {/* ACTION MENU */}
        <Menu
          anchorEl={actionMenuAnchorEl}
          open={Boolean(actionMenuAnchorEl)}
          onClose={handleCloseActionsMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              minWidth: 100,
              maxWidth: 150,        
            },
          }}
        >
          <MenuItem
            onClick={handleRescheduleClick}
            dense
            sx={{
              fontSize: "0.8125rem",
              px: 1.5,
              //color: "#059669",
            }}
          >
            <CalendarTodayOutlinedIcon
              fontSize="small"
              sx={{ mr: 1, fontSize: 16 }}
            />
            Reschedule
          </MenuItem>
        </Menu>

        {/* HEADER */}
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            All Candidate Details
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              //size="small"
              color="info"
              endIcon={<FileDownloadOutlinedIcon />}
              onClick={handleExportToExcel}
              disabled={loading || sortedRows.length === 0}>
              Export to Excel
            </Button>

            <TextField
              size="small"
              placeholder="Search by name"
              value={nameQuery}
              onChange={(e) => {
                setNameQuery(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: 240,
                "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
              }}
            />

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="job-title-filter-label" shrink>
                Job Title
              </InputLabel>
              <Select
                labelId="job-title-filter-label"
                id="job-title-filter"
                value={jobTitleFilter}
                label="Job Title"
                size="small"
                onChange={(e) => {
                  setJobTitleFilter(e.target.value);
                  setPage(0);
                }}
                displayEmpty
                renderValue={(v) =>
                  jobTitleFilterOptions.find((o) => o.value === v)?.label ?? "All"
                }
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: 1.5,
                  fontSize: "0.875rem",
                }}
              >
                {jobTitleFilterOptions.map((opt) => (
                  <MenuItem
                    key={opt.value || "all-job-titles"}
                    value={opt.value}
                    size="small"
                    sx={{ fontSize: "0.875rem" }}
                  >
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="status-filter-label" shrink>
                Status
              </InputLabel>
              <Select
                labelId="status-filter-label"
                id="status-filter"
                value={statusFilter}
                label="Status"
                size="small"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
                displayEmpty
                renderValue={(v) =>
                  statusFilterOptions.find((o) => o.value === v)?.label ?? "All"
                }
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: 1.5,
                  fontSize: "0.875rem",
                }}
              >
                {statusFilterOptions.map((opt) => (
                  <MenuItem
                    key={opt.value || "all-statuses"}
                    value={opt.value}
                    size="small"
                    sx={{ fontSize: "0.875rem" }}
                  >
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {/* <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel shrink>Status</InputLabel>
            <Select
              value={statusFilter}
              //label="Status"
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
            >
              {STATUS_FILTER_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl> */}
        </Box>

        {/* TABLE */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {[
                  { label: "Candidate Name" },
                  { label: "Job Title" },
                  { label: " Interview Status" },
                  { label: "AI Behavior Score" },
                  { label: "AI Response Score" },
                  { label: "Scheduled Date", sortField: "scheduledDate" },
                  { label: "Completed Date", sortField: "completedAt" },
                  { label: "Recording" },
                  { label: "Zoho Sync" },
                  { label: "Actions" },
                ].map((col) => {
                  const isSortActive =
                    col.sortField && sortConfig.field === col.sortField;

                  return (
                    <TableCell
                      key={col.label}
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.secondary,
                        fontSize: "0.875rem",
                      }}
                    >
                      {col.sortField ? (
                        <TableSortLabel
                          active={isSortActive}
                          direction={
                            isSortActive ? sortConfig.direction : "asc"
                          }
                          IconComponent={ArrowDownwardIcon}
                          onClick={() => handleSortByDate(col.sortField)}
                          sx={{
                            color: theme.palette.text.secondary,
                            "&.Mui-active": {
                              color: theme.palette.text.secondary,
                            },
                            "& .MuiTableSortLabel-icon": {
                              opacity: 1,
                              color: `${theme.palette.text.secondary} !important`,
                            },
                            fontWeight: 600,
                            fontSize: "0.875rem",
                          }}
                        >
                          {col.label}
                        </TableSortLabel>
                      ) : (
                        col.label
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading
                ? renderLoading()
                : filteredRows.length === 0
                ? renderEmpty("No candidates match the selected filters.")
                : paginatedRows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ fontWeight: 500,textTransform: "capitalize" }}>
                        {row.candidateName || "-"}
                      </TableCell>

                      <TableCell >{row.jobTitle || "-"}</TableCell>

                      <TableCell>
                        <StatusBadge status={row.status} />
                      </TableCell>

                      <TableCell>{formatScore(row.behaviourScore)}</TableCell>
                      <TableCell>{formatScore(row.answereScore)}</TableCell>

                      <TableCell>
                        {formatDate(getRowDateRaw(row, "scheduledDate")) ||
                          "Not Scheduled"}
                      </TableCell>

                      <TableCell>
                        {formatDate(getRowDateRaw(row, "completedAt")) ? (
                          formatDate(getRowDateRaw(row, "completedAt"))
                        ) : (
                          <span style={{ color: "#a6aebb" }}>
                            Not Completed
                          </span>
                        )}
                      </TableCell>

                      <TableCell>
                        <Button
                          variant="text"
                          startIcon={<PlayCircleOutlineIcon />}
                          disabled={row.status !== "Completed"}
                          sx={{
                            textTransform: "none",
                            color: theme.palette.primary.main,
                            fontWeight: 500,
                          }}
                          onClick={() => {
                            saveDashboardTableState({
                              page: safePage,
                              rowsPerPage,
                              statusFilter,
                              jobTitleFilter,
                              nameQuery,
                              fromDashboard: true,
                            });
                            navigate(`/view-recording/${row.inviteToken || row.id}`, {
                              state: { candidate: row },
                            });
                          }}
                        >
                          View
                        </Button>
                      </TableCell>

                      <TableCell>
                        {row.zohoSyncStatus ? (
                          <ZohoSyncStatus status={row.zohoSyncStatus} />
                        ) : (
                          <span style={{ color: "#a6aebb" }}>
                            Not Synced
                          </span>
                        )}
                      </TableCell>

                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={(e) => handleOpenActionsMenu(e, row)}
                          disabled={
                            row.status !== "Scheduled" &&
                            row.status !== "LinkExpired" &&
                            row.status !== "ReScheduled"
                          }
                          sx={{
                            color:
                              row.status === "Scheduled" ||
                              row.status === "LinkExpired" ||
                              row.status === "ReScheduled"
                                ? theme.palette.text.primary
                                : theme.palette.text.disabled,
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* PAGINATION */}
        {filteredRows.length > 0 && (
          <CommonTablePagination
            count={filteredRows.length}
            page={safePage}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25]}
          />

        )}
      </Paper>
    </Box>
  );
};

export default CandidateInterviewsTable;
